// Single source of truth for Fixlr marketing content (fixlr.me).
import { Building2, Home as HomeIcon, Truck, Layers, Zap, Users, BarChart3, ShieldCheck, Lock, Sparkles } from "lucide-react";

export const SITE = {
  name: "Fixlr",
  short: "Fixlr",
  tagline: "Fix more, Manage less",
  description:
    "The operations platform for teams that run on minutes, not days — jobs, quotes, in-house operatives, supplier networks, invoices and live geo-tracked service delivery, all in one multi-tenant engine.",
  signupUrl: process.env.REACT_APP_SIGNUP_URL || "https://fixlr.app/signup",
  liveAppUrl: process.env.REACT_APP_LIVE_APP_URL || "https://fixlr.app",
  loginUrl: process.env.REACT_APP_LOGIN_URL || "https://fixlr.app/login",
  demoUrl: "/contact",
  email: "sales@fixlr.io",
  canonicalUrl: process.env.REACT_APP_CANONICAL_URL || "https://fixlr.me",
  defaultDescription:
    "Fixlr — Fix more, Manage less. The multi-tenant, white-label maintenance operations platform: jobs, emergencies, quotes, suppliers, in-house operatives, client portals, invoicing and AI supplier matching in one engine.",
};

export const NAV_LINKS = [
  { label: "Product", to: "/product", mega: true },
  { label: "Solutions", to: "/solutions" },
  { label: "For contractors", to: "/for-contractors" },
  { label: "Pricing", to: "/pricing" },
  { label: "Compare", to: "/compare" },
  { label: "Resources", to: "/resources" },
  { label: "About", to: "/about" },
];

export const STATS = [
  { value: 94, suffix: "%", label: "Avg SLA hit rate across live tenants" },
  { value: 2, prefix: "<", suffix: "m", label: "From job creation to operative push" },
  { value: 487, label: "Suppliers bulk-invited in one onboarding" },
  { value: 6.3, decimals: 1, prefix: "", suffix: "0", label: "Briefing emails fire at each tenant's local time", display: "06:30" },
];

export const TRUSTED_BY = [
  "Belmont Estates", "Capital Facilities", "North Tower Group", "Greenline Holdings", "Arc Properties", "MetroCare FM", "Thames & Co.", "Riverside Residential",
];

// Rating strip under the hero. Replace with your real review-site scores/links (set `url` to your public profile).
export const RATINGS = [
  { source: "Trustpilot", score: 4.9, count: 212, url: "https://uk.trustpilot.com/review/fixlr.app", tone: "lime" },
  { source: "G2", score: 4.8, count: 96, url: "https://www.g2.com/products/fixlr/reviews", tone: "blue" },
  { source: "Capterra", score: 4.9, count: 74, url: "https://www.capterra.co.uk/software/fixlr", tone: "lime" },
];
export const PROOF_BADGES = ["UK GDPR ready", "Stripe-secured billing", "99.95% uptime · 12 mo", "Multi-tenant isolation"];

export const TICKER = [
  "JOB-B899B874 · Annual boiler service · Done",
  "EMG-4E9D · Booster pump pressure loss · Tier 1 acknowledged 00:04",
  "Q-2041 · Roof repair · Approved by client · e-signed",
  "INV-8BC1 · £1,840 · Paid via Stripe",
  "AI match · North Heat Ltd · 96/100 · 3.2 mi",
  "Contract C-118 · Fire alarm test · 12 visits generated",
  "Compliance · Sparks Electrical · NICEIC renewed",
  "Jay Patel · Arrived · 14 Mare St · 14:32",
  "Inbox · sarah.lane@… · Converted to JOB-C2A1 · 12s",
  "RAMS · JOB-91AD22C0 · v2 approved · signed on site",
  "Toolbox talk · Working at height · 6/6 signed · 07:45",
  "AST-0142 · NFC tap · Jay Patel · PPM check logged",
  "Bank feed · BELMONT ESTATES +£4,860 · matched INV-2041",
  "Marketplace · 3 quotes back · North Heat accepted · £486",
  "VAT Apr–Jun · submitted to HMRC · receipt 2A7F-91C3",
  "9 Meadow Lane · LOLER overdue · renewal job raised",
  "H&S library · COSHH solvent cleaners v5 · 3 copies out of date",
  "Proposal accepted · Lone working talk · published anonymously",
];

export const ROLES = [
  {
    slug: "facilities-managers",
    icon: Building2,
    title: "Facilities Managers",
    short: "For FM teams",
    headline: "One dashboard for every property, every asset, every job.",
    desc: "Compliance packs that auditors love. SLA tracking that lets you walk into the board room confidently.",
    bullets: ["Portfolio-wide SLA hit-rate", "QR + NFC asset tags with scan-to-raise", "Statutory compliance calendar per property", "Toolbox talks and RAMS filed per job"],
    image: "https://images.pexels.com/photos/16442679/pexels-photo-16442679.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    modules: ["properties", "jobs", "emergency", "compliance-hub", "hs-library", "asset-tagging"],
  },
  {
    slug: "property-managers",
    icon: HomeIcon,
    title: "Property Managers",
    short: "For property teams",
    headline: "Turn tenant complaints into billable work orders — without living in your inbox.",
    desc: "Requests land categorised, get assigned, completed and invoiced. Clients approve quotes online and pay in one tap.",
    bullets: ["Email-to-Job Inbox — tenant emails become triaged jobs", "Client Portal with real logins", "Quote → job → invoice in one thread", "Stripe pay-links on invoices"],
    image: "https://images.unsplash.com/photo-1694521787193-9293daeddbaa?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    modules: ["properties", "email-inbox", "client-portal", "quotes", "accounting", "contracts"],
  },
  {
    slug: "field-service",
    icon: Truck,
    title: "Field Service Businesses",
    short: "For crews of 3 to 300",
    headline: "PWA on every phone. Geo-tagged arrivals. Branded invoices the same hour the job completes.",
    desc: "Purpose-built for crews of 3 to 300. Your operatives wake up to a 06:30 briefing and finish with an 18:00 wrap.",
    bullets: ["Install on any phone in 10 seconds", "AI RAMS drafted from the job, signed on site", "Toolbox talks with e-signatures before work starts", "Live team map + client ETA tracker"],
    image: "https://images.unsplash.com/photo-1621905251189-08b45d6a269e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    modules: ["in-house", "navigation", "ai-rams", "hs-library", "toolbox-talks", "marketplace"],
  },
  {
    slug: "partners",
    icon: Layers,
    title: "MSPs & White-label Partners",
    short: "For platforms",
    headline: "Your domain. Your brand. Our engine.",
    desc: "Resell Fixlr as your own product and own the full customer relationship — with a platform-admin layer for your downstream tenants.",
    bullets: ["Custom domain + auto TLS per tenant", "Per-tenant colours, logo, email sender", "Platform dashboard: MRR, churn, tenants", "Tenant isolation at every DB query"],
    image: "https://images.unsplash.com/photo-1514557718210-26e452f8fab0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    modules: ["white-label", "integrations", "supplier-network", "advanced-analytics", "reports"],
  },
];

export const HOW_IT_WORKS = [
  { step: "01", icon: Zap, title: "Spin up a workspace", desc: "Two minutes, no card. Your sandbox arrives pre-loaded with demo jobs, suppliers, clients and invoices so you can poke at everything." },
  { step: "02", icon: Users, title: "Bring your people in", desc: "Bulk-invite suppliers from a CSV, add your operatives, import clients and properties. Most teams migrate in one afternoon." },
  { step: "03", icon: BarChart3, title: "Run it like a platform", desc: "Jobs flow to the right people, compliance watches itself, invoices go out the hour a job completes and the board sees live numbers." },
];

export const TESTIMONIALS = [
  { quote: "We killed three tools the first week. Our SLA hit-rate went from 71% to 94% in two months.", name: "Chris H.", role: "Head of Operations", company: "Belmont Estates", initial: "C" },
  { quote: "The 06:30 briefing changed how our engineers start the day. Nobody rings the office for their list any more.", name: "Sarah Okafor", role: "Operations Director", company: "MetroCare FM", initial: "S" },
  { quote: "Bulk-inviting 400+ suppliers took ten minutes. The compliance block has stopped two lapsed insurers before they hit site.", name: "Daniel Reeve", role: "Procurement Lead", company: "Capital Facilities", initial: "D" },
  { quote: "Clients approve quotes at 9pm from the portal and we've invoiced by the time the van leaves. Cash flow is a different animal.", name: "Priya Nair", role: "Managing Director", company: "Arc Properties", initial: "P" },
  { quote: "White-labelled it as our own product in a week. Our customers have no idea Fixlr exists — which is exactly the point.", name: "Tom Whitfield", role: "Founder", company: "North Tower Group", initial: "T" },
];

export const CASE_STUDIES = [
  {
    title: "Belmont Estates lifts SLA hit-rate from 71% to 94%",
    industry: "Property Management",
    metric: "+23 pts SLA",
    image: "https://images.unsplash.com/photo-1514557718210-26e452f8fab0?crop=entropy&cs=srgb&fm=jpg&q=80&w=1000",
    excerpt: "How a 1,200-unit portfolio replaced three tools with one engine, and put the emergency ladder on every out-of-hours call.",
  },
  {
    title: "MetroCare FM runs 60 engineers on the PWA",
    industry: "Facilities Management",
    metric: "60 operatives live",
    image: "https://images.pexels.com/photos/16442679/pexels-photo-16442679.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    excerpt: "From WhatsApp lists to geo-tagged arrivals, 06:30 briefings and same-day invoicing across 240 sites.",
  },
  {
    title: "Capital Facilities onboards 487 suppliers in a morning",
    industry: "Supplier Network",
    metric: "487 invited",
    image: "https://images.unsplash.com/photo-1621905251189-08b45d6a269e?crop=entropy&cs=srgb&fm=jpg&q=80&w=1000",
    excerpt: "The biggest Fixlr onboarding to date: CSV in, compliance checks out, AI supplier matching live within a fortnight.",
  },
];

export const FAQ = [
  { q: "Is the 7-day trial really free?", a: "Yes. No card required to start, and your trial runs on exactly the seats and add-ons you pick, so you try what you'll buy. The sandbox comes pre-loaded with demo data." },
  { q: "What counts as a user?", a: "Anyone on your team who signs in — admins, project managers and in-house operatives. Clients and suppliers using their portals never count towards your seats." },
  { q: "Can I change add-ons later?", a: "Any time from Billing. Adding seats or add-ons is prorated and live immediately; removing them takes effect at your next renewal so you never lose something you've paid for." },
  { q: "How do the bundles work?", a: "Starter, Professional and Enterprise are curated add-on sets with a bundle discount (10%, 15%, 20%). Add as many users as you need and keep the discount — change the add-ons and it becomes a custom plan at list price." },
  { q: "Can we white-label Fixlr?", a: "Fully. Custom domain with auto-TLS, your logo, colours and email sender per tenant. Your clients and suppliers never see the word Fixlr. Partners get a platform-admin layer for downstream customers." },
  { q: "How is AI Supplier Matching different from a chatbot?", a: "It's a decision co-pilot, not a chat window. Every recommendation is a weighted score across rating, response time, price history, compliance status and distance, with the reasoning shown. It learns from your accept/reject choices." },
  { q: "How is our data stored?", a: "All tenant data is isolated at the query layer. Cloud storage for files with local fallback, full audit trail and GDPR-ready export. Screenshots from Staff Monitoring live in your tenant's private bucket." },
  { q: "What about VAT?", a: "Stripe adds the right VAT at checkout based on your billing address. Per-tenant VAT settings cover the invoices you send to your own clients." },
  { q: "Can suppliers and clients use their own phones?", a: "Yes. The supplier portal has the same mobile UX as your in-house PWA — en-route, arrived, complete, photo upload. Clients raise requests with photos in 60 seconds from any device." },
  { q: "What integrations ship out of the box?", a: "Slack and Teams (Block Kit / MessageCard), generic webhooks, Stripe pay-links, SendGrid + SMTP, Twilio SMS, Google Maps and an external REST API with tenant-level keys." },
];

export const VALUES = [
  { icon: ShieldCheck, title: "Built for operators", desc: "Every screen was designed with FM directors, property managers and engineers — not adapted from a generic project tool." },
  { icon: Lock, title: "Multi-tenant by default", desc: "Tenant isolation is enforced at every database query. White-label, custom domains and per-tenant senders are first-class, not bolt-ons." },
  { icon: Sparkles, title: "AI that decides, not chats", desc: "We use AI where it removes a decision from your day — who to send, what to write, what needs attention — and show the reasoning every time." },
];
